import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import VoxelAutocompleteCity from '../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCity';
import VoxelAutocompleteCountry, {
  globalOption,
} from '../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteCountry';
import VoxelAutocompleteLanguage, {
  allLanguagesOption,
} from '../../../../components/muiLegacy/Autocomplete/VoxelAutocompleteLanguage';
import DomainForm from '../../../../components/muiLegacy/Forms/DomainForm';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelInput from '../../../../components/voxel/Forms/VoxelInput';
import VoxelSelect from '../../../../components/voxel/Forms/VoxelSelect';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import VoxelDivider from '../../../../components/voxel/VoxelDivider';
import { currencyList } from '../../../../graphql/projects/models/Currencies';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { NewProjectFormState } from './NewProjectFormState';
import { setUpNewProjectForm } from './setUpNewProjectForm';
import { KeywordTableState } from '../../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import VoxelAlertDialog from '../../../../components/voxel/Dialogs/VoxelAlertDialog';
import VoxelLink from '../../../../components/voxel/Link/VoxelLink';
import { ProjectTabChoices } from '../../../ProjectPage/ProjectPage';

export interface NewProjectDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  startValues?: Partial<NewProjectFormState>;
  qSProjectId?: number;
  user: CurrentUserGq;
  keywordTableState?: KeywordTableState;
}

const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  openDialog,
  setOpenDialog,
  startValues,
  qSProjectId,
  user,
  keywordTableState,
}) => {
  const { formBag, fallbackProjectName, targetQSProject } = setUpNewProjectForm(user, startValues, qSProjectId, keywordTableState);

  const [tgQSProject, setTgQSProject] = useState<ProjectGq>(null);
  useEffect(() => setTgQSProject(targetQSProject), [targetQSProject]);     // Only has a return value if QS success

  const handleClose = () => {
    formBag.resetForm();
    setOpenDialog(false);
  };

  const handleCloseSuccess = () => {
    setTgQSProject(null);
    handleClose();
  };

  const showRankSection = useMemo(
    () =>
      formBag.values.language?.id === allLanguagesOption.id ||
      formBag.values.country?.id === globalOption.id,
    [formBag.values.language, formBag.values.country]
  );

  return (
    <>
      <VoxelDialog
        title={'New project'}
        subtitle={qSProjectId ? 'Selected keywords will be added to the new project' : null}
        open={openDialog}
        onClose={handleClose}
        minWClass="min-w-[700px]"
      >
        <div className="pt-2">
          <VoxelDialogContent>
            <VoxelInput
              label="Name"
              id="projectName"
              labelPosition="top"
              autoFocus
              value={formBag.values.projectName}
              onChange={(event) => formBag.setFieldValue('projectName', event.target.value)}
              deleteValue={() => formBag.setFieldValue('projectName', '')}
              placeholder={fallbackProjectName}
              maxLength={500}
              disabled={formBag.isSubmitting}
              helperText={formBag.errors?.projectName}
              showHelperText={!!formBag.errors?.projectName?.length && formBag.submitCount > 0}
            />
          </VoxelDialogContent>
          <VoxelDivider />
          <VoxelDialogContent>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-4">
                <div className="h-2" />
                <VoxelText>Keyword settings</VoxelText>
                <div className="">
                  <VoxelAutocompleteLanguage
                    type="both"
                    labelPosition="top"
                    selectedLanguage={formBag.values?.language}
                    setSelectedLanguage={(language) => formBag.setFieldValue('language', language)}
                    disabled={formBag.isSubmitting || formBag.values.isQuickSearch}
                    hasLock={
                      formBag.values.isQuickSearch ? 'Setting cannot be changed' : null
                    }
                  />
                </div>
                <div className="flex flex-row gap-x-4">
                  <div
                    className={
                      formBag.values.country?.id !== globalOption?.id ? 'basis-1/2' : 'basis-full'
                    }
                  >
                    <VoxelAutocompleteCountry
                      type="both"
                      labelPosition="top"
                      selectedCountry={formBag.values.country}
                      setSelectedCountry={(country) => formBag.setFieldValue('country', country)}
                      clearSelectedCity={() => formBag.setFieldValue('city', null)}
                      disabled={formBag.isSubmitting || formBag.values.isQuickSearch}
                      hasLock={
                        formBag.values.isQuickSearch ? 'Setting cannot be changed' : null
                      }
                      outlined
                    />
                  </div>
                  {formBag.values.country?.id !== globalOption?.id && (
                    <div className="basis-1/2">
                      <VoxelAutocompleteCity
                        type="both"
                        labelPosition="top"
                        selectedCountry={formBag.values.country}
                        selectedCity={formBag.values.city}
                        setSelectedCity={(city) => formBag.setFieldValue('city', city)}
                        disabled={
                          formBag.isSubmitting ||
                          formBag.values.country?.id === globalOption?.id ||
                          formBag.values.isQuickSearch
                        }
                        outlined
                      />
                    </div>
                  )}
                </div>

                <div>
                  <VoxelSelect
                    id="currency"
                    label="Currency"
                    labelPosition="top"
                    options={currencyList.map((currency) => ({
                      title: currency.code + ' (' + currency.symbol + ')',
                      value: currency.code,
                    }))}
                    selectedOptionValue={formBag.values?.currency?.code}
                    helperText={formBag.errors?.currency as any}
                    showHelperText={!!(formBag.errors?.currency as any)?.length}
                    setSelectedOption={(value) =>
                      formBag.setFieldValue(
                        'currency',
                        currencyList.find((currency) => currency.code === value.value)
                      )
                    }
                    disabled={formBag.isSubmitting}
                  />
                </div>
              </div>
              {showRankSection && (
                <div className="flex flex-col gap-4 rounded-lg -m-3 p-3 -mt-2 pt-2 -mb-4 pb-4  bg-voxel-blue-50 ">
                  <VoxelText variant="title3">
                    Rank data must have a specified language and location
                  </VoxelText>
                  {formBag.values.language?.id === allLanguagesOption?.id && (
                    <div className="">
                      <VoxelAutocompleteLanguage
                        type="rank"
                        labelPosition="top"
                        selectedLanguage={formBag.values?.serpLanguage}
                        setSelectedLanguage={(language) =>
                          formBag.setFieldValue('serpLanguage', language)
                        }
                        disabled={formBag.isSubmitting}
                      />
                    </div>
                  )}
                  {formBag.values.country?.id === globalOption?.id && (
                    <div className="flex flex-row gap-x-4">
                      <div className="basis-1/2">
                        <VoxelAutocompleteCountry
                          type="rank"
                          labelPosition="top"
                          selectedCountry={formBag.values.serpCountry}
                          setSelectedCountry={(country) =>
                            formBag.setFieldValue('serpCountry', country)
                          }
                          clearSelectedCity={() => formBag.setFieldValue('serpCity', null)}
                          disabled={formBag.isSubmitting}
                          outlined
                        />
                      </div>
                      <div className="basis-1/2">
                        <VoxelAutocompleteCity
                          type="rank"
                          labelPosition="top"
                          selectedCountry={formBag.values.serpCountry}
                          selectedCity={formBag.values.serpCity}
                          setSelectedCity={(city) => formBag.setFieldValue('serpCity', city)}
                          disabled={formBag.isSubmitting || !formBag.values.serpCountry}
                          outlined
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </VoxelDialogContent>
          <VoxelDivider />
          <VoxelDialogContent>
            <div className="w-full flex flex-col gap-4">
              <div className="h-2" />
              <VoxelText>Project domain</VoxelText>
              <DomainForm
                protocolValue={formBag.values.domain.protocol}
                setProtocolValue={(value) =>
                  formBag.setValues((prev) => {
                    return { ...prev, domain: { ...prev.domain, protocol: value } };
                  })
                }
                domainValue={formBag.values.domain.domain}
                setDomainFormState={(valueFunc) =>
                  formBag.setValues((prev) => {
                    return { ...prev, domain: valueFunc(prev.domain) };
                  })
                }
                deleteDomainValue={() =>
                  formBag.setValues((prev) => {
                    return { ...prev, domain: { ...prev.domain, domain: '' } };
                  })
                }
                disabled={formBag.isSubmitting}
                placeholder={'www.organization.com/se-sv'}
                helperText={formBag.errors?.domain?.domain}
                showHelperText={formBag.errors?.domain?.domain.length > 0 && formBag.submitCount > 0}
              />
            </div>
          </VoxelDialogContent>
        </div>
        <VoxelDialogActions>
          <VoxelDialogActionCancel onClick={handleClose} disabled={formBag.isSubmitting} />
          <VoxelDialogActionSubmit
            title={'Continue'}
            onClick={() => {
              formBag.submitForm();

              //  handleClose();

            }}
            disabled={formBag.isSubmitting}
            type="button"
          />
        </VoxelDialogActions>
      </VoxelDialog>
      {tgQSProject != null && (
        <VoxelAlertDialog title='Success' severity='success' open={tgQSProject != null} onClose={handleCloseSuccess}>
          <VoxelDialogContent className='mt-4 !pb-12'>
            <VoxelText>The keywords were successfully added to the project <VoxelLink openInNewIcon openInNewTab href={`/${tgQSProject?.id}/${ProjectTabChoices.Manage}`}>
              {tgQSProject?.name}
            </VoxelLink>
            </VoxelText>
          </VoxelDialogContent>
        </VoxelAlertDialog>
      )}
    </>
  );
};

export default NewProjectDialog;
